import mapboxgl from 'mapbox-gl';
// Contexts
import { MapEventTypes } from 'src/@realtime/contexts/map';

// Functions
import {
  queryMapFeaturesById,
  queryMapFeaturesByIds,
  calculateVelocityKn,
} from 'src/@realtime/utils';

// Constants
import { RealtimeLayerIds, RealtimeMapEventIds, AltitudeSource } from 'src/@realtime/constants';
import { FlightPosition } from 'src/@realtime/contexts';

// Types
import { FlightDataActionType } from 'src/@realtime/contexts';
import { TailLineStringProperties } from 'src/@realtime/types/flight';

// Utils
import { findNearestPoint } from 'src/@realtime/utils';
import { flattenGeoJSONLineFeature } from 'src/@realtime/utils/multiLineStrings';

export const createClickFunction = (flightDispatch: React.Dispatch<any>) => ({
  id: RealtimeMapEventIds.flightClick,
  type: MapEventTypes.CLICK,
  event: (event: mapboxgl.MapLayerMouseEvent) => {
    const { target, point } = event;
    const features = queryMapFeaturesById(target, point, RealtimeLayerIds.flightLayer);
    if (features[0]) {
      console.log('Selected track:', features[0].properties);
      flightDispatch({
        type: FlightDataActionType.SELECT_TRACK,
        payload: features[0].properties?.trackId as string,
      });
    }
  },
});

export const createHoverFunction = (setTooltip: React.SetStateAction<any>, findPoint: any) => ({
  id: RealtimeMapEventIds.flightHover,
  type: MapEventTypes.HOVER,
  event: (event: mapboxgl.MapLayerMouseEvent) => {
    const { target, point } = event;
    const features = queryMapFeaturesByIds(target, point, [
      RealtimeLayerIds.flightLayer,
      RealtimeLayerIds.hiddenTailLayer,
    ]); // Add a layer for aircraft
    if (features[0]) {
      if (features[0].properties) {
        if (features[0].geometry.type === 'Point') {
          const feature = (features[0] as unknown) as GeoJSON.Feature<
            GeoJSON.Point,
            {
              altitude: number;
              trackId: string;
            }
          >;
          setTooltip({
            coordinates: feature.geometry.coordinates,
            altitude: null,
            altitudeSource: AltitudeSource.dynamic,
            trackId: feature.properties.trackId,
          });
        } else if (
          features[0].geometry.type === 'LineString' ||
          features[0].geometry.type === 'MultiLineString'
        ) {
          const feature = (features[0] as unknown) as GeoJSON.Feature<
            GeoJSON.LineString | GeoJSON.MultiLineString, // Either LineString's or at close zoom levels gemetries split and they're MultiLineString's.
            TailLineStringProperties
          >;

          // Converts MultiLineString to LineString.
          const flatFeature = flattenGeoJSONLineFeature(feature);

          const [pointPosition, prevPosition] = findNearestPoint(event, flatFeature);

          // Find the previous and current flight positions to work out speed
          const flightPosition: FlightPosition = findPoint(
            flatFeature.properties.trackId,
            event,
            flatFeature
          ) || { time: 0, latitude: 0, longitude: 0, altitude: 0, heading: 0 };

          if (!prevPosition) {
            setTooltip({
              coordinates: pointPosition,
              altitude: flightPosition.altitude,
              altitudeSource: AltitudeSource.provided,
              trackId: flatFeature.properties.trackId,
              speed: null,
            });
            return;
          }

          const prevFlightPosition: FlightPosition = prevPosition
            ? findPoint(
                flatFeature.properties.trackId,
                ({
                  lngLat: new mapboxgl.LngLat(prevPosition[0], prevPosition[1]),
                } as unknown) as mapboxgl.MapMouseEvent,
                flatFeature
              ) || { time: 0, latitude: 0, longitude: 0, altitude: 0, heading: 0 }
            : { time: 0, latitude: 0, longitude: 0, altitude: 0, heading: 0 };

          const point1 =
            prevFlightPosition && prevPosition
              ? {
                  latitude: prevPosition[0],
                  longitude: prevPosition[1],
                  altitude: prevFlightPosition.altitude,
                  time: prevFlightPosition.time,
                }
              : null;

          const point2 =
            flightPosition && pointPosition
              ? {
                  latitude: pointPosition[0],
                  longitude: pointPosition[1],
                  altitude: flightPosition.altitude,
                  time: flightPosition.time,
                }
              : null;

          // Call calculateVelocityKn with properly formatted objects
          let speed: number | null = null;
          if (point1 && point2) {
            speed = calculateVelocityKn(point1, point2);
          }

          setTooltip({
            coordinates: pointPosition,
            altitude: flightPosition.altitude,
            altitudeSource: AltitudeSource.provided,
            trackId: flatFeature.properties.trackId,
            speed,
          });
        }
      }
    } else {
      setTooltip(null);
    }
  },
});
